import React, { useEffect } from 'react';
import {
  Shield,
  Mail,
  Bell,
  Lock,
  Eye,
  Download,
  Trash2,
  Ban,
  Cookie,
  Globe,
} from 'lucide-react';
import './PrivacyPolicy.css';
import usePageTitle from '../hooks/usePageTitle';

const EFFECTIVE_DATE = 'June 3, 2026';

const PrivacyPolicy: React.FC = () => {
  usePageTitle('Privacy Policy - The Kults');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="privacy-policy-page">
      <div className="privacy-policy-container">
        <header className="privacy-header">
          <div className="privacy-icon">
            <Shield size={48} />
          </div>
          <h1>Privacy Policy</h1>
          <p className="last-updated">Last updated: {EFFECTIVE_DATE}</p>
        </header>

        <div className="privacy-content">
          <section className="policy-section">
            <div className="policy-summary">
              <h2 className="policy-summary-title">The short version</h2>
              <ul>
                <li>
                  We&apos;re a small independent band. We only collect the
                  information you choose to give us &mdash; your email if you
                  subscribe to our newsletter, and your name, email, and message
                  if you contact us.
                </li>
                <li>
                  We do <strong>not</strong> run analytics, advertising, or
                  third-party tracking. We don&apos;t build profiles, and we
                  never sell or rent your information.
                </li>
                <li>
                  The only thing we store in your browser is your cookie choice,
                  so we don&apos;t ask you again.
                </li>
                <li>
                  You can unsubscribe or ask us to delete your data at any time
                  through our <a href="/contact">contact form</a>.
                </li>
              </ul>
            </div>
          </section>

          <section className="policy-section">
            <h2>1. Who we are &amp; what this covers</h2>
            <p>
              This Privacy Policy explains how The Kults (&quot;we,&quot;
              &quot;our,&quot; or &quot;us&quot;) handles personal information
              when you use our website at thekults.com. We are an independent
              music band based in Basel, Switzerland, with an audience primarily
              in the United States and Europe.
            </p>
            <p>
              We&apos;ve written this in plain language on purpose. If anything
              here is unclear, please reach out through our{' '}
              <a href="/contact">contact form</a> and we&apos;ll explain.
            </p>
          </section>

          <section className="policy-section">
            <h2>2. Information we collect</h2>
            <p>
              We only collect information you actively provide. We don&apos;t
              require accounts and we don&apos;t track you around the web.
            </p>

            <h3>2.1 Information you give us</h3>
            <ul>
              <li>
                <strong>Newsletter:</strong> your email address, and the fact
                that you consented to receive emails.
              </li>
              <li>
                <strong>Contact form:</strong> your name, email address,
                subject, and the message you send us.
              </li>
            </ul>

            <h3>2.2 Limited technical information</h3>
            <p>
              When you submit one of the forms above, a small amount of context
              is sent with it &mdash; such as which page you submitted from and,
              if you arrived via a campaign link, basic referral parameters.
              This helps us understand how people reach us and respond properly.
              We do not use this to identify you or to track your activity.
            </p>

            <h3>2.3 What we do NOT collect</h3>
            <ul>
              <li>No advertising or marketing tracking cookies.</li>
              <li>No cross-site tracking, and no profiling.</li>
              <li>No selling, renting, or sharing of your data for profit.</li>
              <li>No sensitive personal data.</li>
            </ul>
            <p>
              We may use privacy-friendly, cookieless analytics (such as Umami)
              to understand which pages people enjoy. It collects no personal
              data and never tracks you across other sites &mdash; and it only
              runs if you opt in.
            </p>
          </section>

          <section className="policy-section">
            <h2>3. How &amp; why we use your information</h2>
            <p>
              We use the limited information we collect only for the reasons
              below:
            </p>

            <div className="usage-grid">
              <div className="usage-item">
                <Mail size={24} />
                <h4>Respond to you</h4>
                <p>
                  To read and reply to the messages you send us through the
                  contact form.
                </p>
              </div>
              <div className="usage-item">
                <Bell size={24} />
                <h4>Send the newsletter</h4>
                <p>
                  To send you band news and updates &mdash; only if you asked us
                  to, and only until you unsubscribe.
                </p>
              </div>
              <div className="usage-item">
                <Lock size={24} />
                <h4>Keep things working</h4>
                <p>
                  To operate the site, prevent abuse or spam, and meet our legal
                  obligations.
                </p>
              </div>
            </div>

            <h3>3.1 Our legal bases (for EU/UK visitors)</h3>
            <p>
              Under the GDPR and UK GDPR, we rely on the following legal bases:
            </p>
            <ul>
              <li>
                <strong>Consent</strong> &mdash; for sending you the newsletter.
                You can withdraw consent at any time.
              </li>
              <li>
                <strong>Legitimate interests</strong> &mdash; for responding to
                messages you send us and keeping the site secure.
              </li>
              <li>
                <strong>Legal obligation</strong> &mdash; where we must keep or
                disclose information to comply with the law.
              </li>
            </ul>
          </section>

          <section className="policy-section">
            <h2>4. Cookies &amp; local storage</h2>
            <p>
              We keep cookies to an absolute minimum. We do not use advertising
              or third-party cross-site tracking cookies.
            </p>
            <div className="usage-grid">
              <div className="usage-item">
                <Cookie size={24} />
                <h4>Essential only</h4>
                <p>
                  We store your cookie/consent choice in your browser&apos;s
                  local storage so we don&apos;t ask you on every visit.
                </p>
              </div>
              <div className="usage-item">
                <Eye size={24} />
                <h4>Optional analytics</h4>
                <p>
                  Privacy-friendly, cookieless analytics (Umami) that you can
                  turn on or off from the cookie banner. No personal data, no
                  ads.
                </p>
              </div>
            </div>
          </section>

          <section className="policy-section">
            <h2>5. Who we share it with</h2>
            <p>
              We never sell or rent your information. We only share it with a
              small number of trusted service providers who help us run the
              website and communicate with you:
            </p>
            <ul>
              <li>
                <strong>Hosting &amp; form processing:</strong> Google Cloud
                Platform, which stores and processes contact-form and newsletter
                submissions on our behalf.
              </li>
              <li>
                <strong>Email delivery:</strong> the email service we use to
                send newsletters and read your messages.
              </li>
            </ul>
            <p>
              These providers only act on our instructions. We may also disclose
              information if required by law or to protect our legal rights.
            </p>
          </section>

          <section className="policy-section">
            <h2>6. Where your data is processed</h2>
            <p>
              Our service providers may process your information in the United
              States and other countries outside the European Economic Area
              (EEA) and the United Kingdom.
            </p>
            <p>
              Where we transfer personal data from the EEA or UK, we rely on
              recognized safeguards &mdash; such as the European Commission&apos;s
              Standard Contractual Clauses &mdash; to protect it. You can ask us
              for more detail through our <a href="/contact">contact form</a>.
            </p>
          </section>

          <section className="policy-section">
            <h2>7. How long we keep it</h2>
            <ul>
              <li>
                <strong>Newsletter email:</strong> until you unsubscribe or ask
                us to delete it.
              </li>
              <li>
                <strong>Contact messages:</strong> for as long as needed to
                handle your inquiry and a reasonable period afterward, then
                deleted.
              </li>
            </ul>
            <p>
              If you ask us to delete your information, we&apos;ll do so unless
              we&apos;re legally required to keep it.
            </p>
          </section>

          <section className="policy-section">
            <h2>8. Your privacy rights</h2>
            <p>
              Depending on where you live, you have rights over your personal
              information. We&apos;re happy to honor these requests for everyone,
              wherever you are:
            </p>

            <div className="rights-grid">
              <div className="right-item">
                <Eye size={24} />
                <h4>Access</h4>
                <p>Ask what information we hold about you.</p>
              </div>
              <div className="right-item">
                <Download size={24} />
                <h4>Portability</h4>
                <p>Receive a copy of your data.</p>
              </div>
              <div className="right-item">
                <Trash2 size={24} />
                <h4>Deletion</h4>
                <p>Ask us to delete your information.</p>
              </div>
              <div className="right-item">
                <Ban size={24} />
                <h4>Object / Withdraw</h4>
                <p>Opt out or withdraw consent at any time.</p>
              </div>
            </div>

            <p>
              To exercise any of these, contact us through our{' '}
              <a href="/contact">contact form</a>. To stop receiving the
              newsletter, you can also use the unsubscribe link in any email we
              send. We aim to respond within 30 days, free of charge.
            </p>
            <p>
              If you&apos;re in the EEA or UK and believe we haven&apos;t handled
              your data properly, you have the right to lodge a complaint with
              your local data protection authority.
            </p>
          </section>

          <section className="policy-section">
            <h2>9. Your U.S. privacy rights</h2>
            <p>
              If you&apos;re a resident of a U.S. state with a privacy law (such
              as California), you have the right to know what personal
              information we collect, to request its deletion, and to not be
              discriminated against for exercising these rights.
            </p>
            <p>
              We make this simple:{' '}
              <strong>
                we do not sell or share your personal information
              </strong>
              , and we don&apos;t use it for cross-context behavioral
              advertising. To make a request, use our{' '}
              <a href="/contact">contact form</a>.
            </p>
          </section>

          <section className="policy-section">
            <h2>10. Children&apos;s privacy</h2>
            <p>
              Our website isn&apos;t directed at children. We don&apos;t
              knowingly collect personal information from anyone under 16. If you
              believe a child has provided us information, please contact us and
              we&apos;ll delete it.
            </p>
          </section>

          <section className="policy-section">
            <h2>11. Changes to this policy</h2>
            <p>
              We may update this policy from time to time. If we make meaningful
              changes, we&apos;ll update the &quot;Last updated&quot; date above
              and post the new version on this page.
            </p>
          </section>

          <section className="policy-section">
            <h2>12. Contact us</h2>
            <p>
              Questions about your privacy, or want to exercise any of your
              rights? Get in touch:
            </p>

            <div className="contact-info">
              <div className="contact-item">
                <Mail size={20} />
                <strong>Contact form:</strong>
                <a href="/contact">Send us a message</a>
              </div>
              <div className="contact-item">
                <Globe size={20} />
                <strong>Based in:</strong>
                <span>Basel, Switzerland</span>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
